import styles from "./UploadPage.module.css";

import api from "../../api/api";
import Icon from "../../components/Icon/Icon";
import StatusModal from "../../components/StatusModal/StatusModal";
import { useState } from "react";

import Loader from "../../components/Loader/Loader";

const UploadPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Фильтрация только PDF-файлов
  const filterPdfFiles = (files: File[]) => {
    return files.filter((file) => file.name.toLowerCase().endsWith(".pdf"));
  };

  // Обновленная функция загрузки с обработкой ошибок по каждому файлу
  const handleUpload = async (files: File[]) => {
    const failed: string[] = [];
    // Step 1: Get presigned URLs and upload files to S3 concurrently (но с обработкой ошибок)
    const s3UploadPromises = files.map(async (file) => {
      try {
        const preSignData = await api.preSignFile(file); // Get URL and key
        await api.uploadPreSignDataToS3(file, preSignData.url); // Upload to S3 using the URL
        return {
          documentId: preSignData.documentId,
          fileName: file.name,
          error: null,
        };
      } catch {
        failed.push(file.name);
        return { documentId: null, fileName: file.name, error: true };
      }
    });

    const s3UploadResponses = await Promise.all(s3UploadPromises);
    const presignedIds = s3UploadResponses
      .filter((r) => r.documentId !== null)
      .map((r) => r.documentId as string);

    // Step 2: Confirm OCR upload for each file sequentially
    for (const id of presignedIds) {
      try {
        await api.uploadOcrFile(id);
      } catch {
        const fileObj = s3UploadResponses.find((r) => r.documentId === id);
        if (fileObj) failed.push(fileObj.fileName);
      }
    }
    return failed;
  };

  // Обновленная функция выбора файлов
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    // Если выбрана папка (webkitdirectory), фильтруем только PDF
    const inputEl = e.target as HTMLInputElement & {
      webkitdirectory?: boolean;
    };
    if (inputEl.webkitdirectory) {
      files = filterPdfFiles(files);
    }

    if (files.length === 0) {
      alert("No hay archivos PDF seleccionados");
      return;
    }

    try {
      setIsLoading(true);
      const failed = await handleUpload(files);
      setIsModalOpen(true);
      if (failed.length > 0) {
        alert(
          `Los siguientes archivos no pudieron ser cargados: \n${failed.join(
            "\n"
          )}`
        );
      }
    } catch (error) {
      alert("Error al cargar el documento, inténtelo de nuevo más tarde");
      console.error("Error while uploading the files: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && (
        <Loader>
          <p className={styles.warningMessage}>
            Espere a que finalice la descarga, no cierre ni recargue esta
            página.
          </p>
        </Loader>
      )}
      <div className={styles.container}>
        <h1 className={styles.title}>Importación de documentos</h1>
        <p className={styles.description}>
          Puedes cargar documentos de forma individual o en bloque, y el sistema
          se encargará de organizarlos por tipo y expediente automáticamente
        </p>

        <div className={styles.actionsContainer}>
          <div className={styles.labelBorder}>
            <label className={styles.inputLabel}>
              <span className={styles.gradientText}>Subir archivo</span>{" "}
              <Icon
                size={22}
                name="file"
                className={styles.uploadIcon}
                gradient={true}
              />
              <input
                type="file"
                multiple
                accept=".pdf"
                onChange={handleFileChange}
                className={styles.hiddenInput}
              />
            </label>
          </div>

          <span className={styles.divider}></span>

          <div className={styles.labelBorder}>
            <label className={styles.inputLabel}>
              <span className={styles.gradientText}>Subir carpeta</span>{" "}
              <Icon
                size={22}
                name="folder"
                className={styles.uploadIcon}
                gradient={true}
              />
              <input
                type="file"
                multiple
                {...{ webkitdirectory: "true", directory: "true" }}
                accept=".pdf"
                onChange={handleFileChange}
                className={styles.hiddenInput}
              />
            </label>
          </div>
        </div>
      </div>
      <StatusModal
        isOpen={isModalOpen}
        onRequestClose={() => {
          setIsModalOpen(false);
        }}
      >
        <p className={styles.modalText}>
          {" "}
          ¡Los documentos se han cargado correctamente! ¡Recibirá una
          notificación cuando se hayan procesado por completo!
        </p>
      </StatusModal>
    </>
  );
};

export default UploadPage;
